import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useSportContent } from '../hooks/useSportContent';

interface AssessmentResult {
  assessmentId: string;
  score: number;
  totalQuestions: number;
  percentage: number;
  completedAt: string;
  answers: { questionId: string; selectedAnswer: number; isCorrect: boolean }[];
}

const ProfileScreen: React.FC = () => {
  const { currentSport } = useTheme();
  const { user, loading } = useAuth();
  const { content } = useSportContent(currentSport.id);
  const [assessmentResults, setAssessmentResults] = useState<{ [sportId: string]: AssessmentResult[] }>({});

  useEffect(() => {
    if (user) {
      // Load assessment results for all sports
      const allResults: { [sportId: string]: AssessmentResult[] } = {};
      const sports = ['pickleball', 'tennis', 'golf', 'hiking', 'padel', 'badminton', 'racquetball', 'squash'];
      
      sports.forEach(sportId => {
        const savedResults = localStorage.getItem(`assessment-results-${user.id}-${sportId}`);
        console.log(`Loading results for ${sportId}:`, savedResults);
        if (savedResults) {
          allResults[sportId] = JSON.parse(savedResults);
        }
      });
      
      console.log('All assessment results loaded:', allResults);
      setAssessmentResults(allResults);
    }
  }, [user]);

  const getSportName = (sportId: string) => {
    switch (sportId) {
      case 'pickleball': return 'Pickleball';
      case 'tennis': return 'Tennis';
      case 'golf': return 'Golf';
      case 'hiking': return 'Hiking';
      case 'padel': return 'Padel';
      case 'badminton': return 'Badminton';
      case 'racquetball': return 'Racquetball';
      case 'squash': return 'Squash';
      default: return sportId;
    }
  };

  const getAssessmentName = (assessmentId: string, sportId: string) => {
    // This would ideally load the assessment content, but for now we'll use a simple mapping
    const assessmentNames: { [key: string]: string } = {
      'pickleball-beginner-assessment': 'Beginner Assessment',
      'pickleball-intermediate-assessment': 'Intermediate Assessment',
      'pickleball-advanced-assessment': 'Advanced Assessment',
      'pickleball-technique-assessment': 'Technique Assessment',
      'tennis-beginner-assessment': 'Beginner Assessment',
      'tennis-intermediate-assessment': 'Intermediate Assessment',
      'tennis-advanced-assessment': 'Advanced Assessment',
      'tennis-technique-assessment': 'Technique Assessment',
      'golf-beginner-assessment': 'Beginner Assessment',
      'golf-intermediate-assessment': 'Intermediate Assessment',
      'golf-advanced-assessment': 'Advanced Assessment',
      'golf-technique-assessment': 'Technique Assessment'
    };
    return assessmentNames[assessmentId] || 'Unknown Assessment';
  };

  const getOverallProgress = (sportId: string) => {
    const results = assessmentResults[sportId] || [];
    if (results.length === 0) return 0;
    
    const totalScore = results.reduce((sum, result) => sum + result.percentage, 0);
    return Math.round(totalScore / results.length);
  };

  if (loading) {
    return (
      <div className="profile-screen">
        <div className="container">
          <h1>Profile</h1>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="profile-screen">
        <div className="container">
          <h1>Profile</h1>
          <p>Please sign in to view your profile and progress.</p>
          <div style={{ marginTop: '1.5rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <Link to="/signin" style={{ padding: '0.5rem 1rem', background: 'var(--primary-color, #2E7D32)', color: 'white', borderRadius: '8px', textDecoration: 'none', fontWeight: 600 }}>
              Sign In
            </Link>
            <Link to="/signup" style={{ padding: '0.5rem 1rem', border: '2px solid var(--primary-color, #2E7D32)', color: 'var(--primary-color, #2E7D32)', borderRadius: '8px', textDecoration: 'none', fontWeight: 600 }}>
              Create Account
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-screen">
      <div className="container">
        <h1>Your Profile</h1>
        <div className="profile-header">
          <div className="user-info">
            <h2>Welcome, {user.email}!</h2>
            <p>Track your progress across all sports</p>
          </div>
        </div>

        <div className="sports-progress">
          <h2>Your Progress by Sport</h2>
          <div className="sports-grid">
            {['pickleball', 'tennis', 'golf'].map(sportId => {
              const results = assessmentResults[sportId] || [];
              const overallProgress = getOverallProgress(sportId);
              const sportName = getSportName(sportId);
              
              return (
                <div key={sportId} className="sport-progress-card">
                  <h3>{sportName}</h3>
                  <div className="progress-circle">
                    <div className="progress-percentage">{overallProgress}%</div>
                    <div className="progress-label">Overall</div>
                  </div>
                  <div className="assessment-count">
                    {results.length} assessment{results.length !== 1 ? 's' : ''} completed
                  </div>
                  {results.length > 0 && (
                    <div className="recent-results">
                      <h4>Recent Results</h4>
                      {results.slice(-3).map((result, index) => (
                        <div key={index} className="result-summary">
                          <span className="assessment-name">
                            {getAssessmentName(result.assessmentId, sportId)}
                          </span>
                          <span className="result-score">
                            {result.percentage}%
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="detailed-results">
          <h2>Detailed Assessment Results</h2>
          {Object.entries(assessmentResults).map(([sportId, results]) => {
            if (results.length === 0) return null;
            
            return (
              <div key={sportId} className="sport-results-section">
                <h3>{getSportName(sportId)} Results</h3>
                <div className="results-timeline">
                  {results.map((result, index) => (
                    <div key={index} className="result-item">
                      <div className="result-header">
                        <span className="assessment-name">
                          {getAssessmentName(result.assessmentId, sportId)}
                        </span>
                        <span className="result-date">
                          {new Date(result.completedAt).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="result-details">
                        <div className="score-breakdown">
                          <span className="score">{result.score}/{result.totalQuestions}</span>
                          <span className="percentage">{result.percentage}%</span>
                        </div>
                        <div className="progress-bar">
                          <div 
                            className="progress-fill" 
                            style={{ width: `${result.percentage}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {Object.values(assessmentResults).every(results => results.length === 0) && (
          <div className="no-results">
            <h3>No Assessment Results Yet</h3>
            <p>Complete some skill assessments to track your progress!</p>
            <p>Go to the Skill Assessment section to get started.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileScreen;
